import { ProductDocument, ProductLight } from 'models/product';
import { Bike } from 'models/shared';

// the server should be doing this, this is for testing
export const getProductLight = (
  doc: ProductDocument,
  variationImages?: string[]
): ProductLight => {
  const {
    fitting_bikes,
    variations,
    description,
    description_html,
    shipment,
    ...rest
  } = doc;
  const productLight: ProductLight = rest;
  if (fitting_bikes?.universal) {
    Object.assign(productLight, {
      fitting_bikes: { bikes: [], universal: true },
    });
  }
  productLight.variation_pricing = getVariationPriceRange(doc);
  if (variationImages?.length)
    productLight.variation_images = variationImages.slice(0, 4);
  else {
    const variationImages: string[] = [];
    variations?.forEach((v) => {
      if (v.image) variationImages.push(v.image);
    });
    productLight.variation_images = variationImages;
  }
  return productLight;
};

export const getVariationPriceRange = (product: ProductDocument) => {
  if (!product.variations?.length) return;
  if (product.category2 === 'Gear Combos') {
    const filteredJerseyPrices: number[] = [];
    const filteredPantsPrices: number[] = [];
    product.variations.forEach((v) => {
      if (!v.price || v.qty <= 0) return;
      if (v.is_number) filteredPantsPrices.push(v.price);
      else filteredJerseyPrices.push(v.price);
    });
    if (filteredJerseyPrices.length && filteredPantsPrices.length) {
      const minJerseyPrice = Math.min(...filteredJerseyPrices);
      const maxJerseyPrice = Math.max(...filteredJerseyPrices);
      const minPantsPrice = Math.min(...filteredPantsPrices);
      const maxPantsPrice = Math.max(...filteredPantsPrices);
      const minPrice = minJerseyPrice + minPantsPrice;
      const maxPrice = maxJerseyPrice + maxPantsPrice;
      if (minPrice === maxPrice) return;
      return { minPrice, maxPrice };
    }
  }
  const filteredPrices = product.variations
    .filter((v) => v.price && v.qty > 0)
    .map((v) => v.price);

  if (!filteredPrices.length) return;

  const minPrice = Math.min(...filteredPrices);
  const maxPrice = Math.max(...filteredPrices);
  if (minPrice === maxPrice) return;

  return { minPrice, maxPrice };
};

function normalizeTitle(value: string): string {
  return value.toUpperCase().replace(/[^A-Z0-9]/g, '');
}

// find top 12 most relevant fitment based on product title and year density
export function getFitmentTokens(args: {
  bikes: Bike[];
  productTitle: string;
  max?: number;
}): string[] {
  const max = args.max ?? 12;
  const title = normalizeTitle(args.productTitle);

  const models = new Map<
    string,
    {
      make: string;
      model: string;
      count: number;
    }
  >();

  for (const bike of args.bikes) {
    if (!bike.make || !bike.model) continue;

    const key = `${bike.make}|${bike.model}`;

    const existing = models.get(key);

    if (existing) {
      existing.count++;
    } else {
      models.set(key, {
        make: bike.make,
        model: bike.model,
        count: 1,
      });
    }
  }

  return [...models.values()]
    .map((entry) => {
      // Normalize only for comparing against messy product title text.
      const modelForTitleMatch = normalizeTitle(entry.model);

      return {
        ...entry,
        score:
          (title.includes(modelForTitleMatch) ? 1000 : 0) +
          Math.min(entry.count, 100),
      };
    })
    .sort((a, b) => b.score - a.score)
    .slice(0, max)
    .map(({ make, model }) => `${make}|${model}`);
}

export function typesenseArrayFilter(
  field: string,
  values: string[]
): string | undefined {
  if (!values.length) return;

  const escaped = values.map((value) => `\`${value.replace(/`/g, '\\`')}\``);

  return `${field}:[${escaped.join(',')}]`; // fuzzy
}
